/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import type { ShopperSearch } from '@/scapi';
import ProductTileCard from '@/components/product-tile-card';
import TilePrice from '@/components/product-tile-card/tile-price';
import { toProductTileCardProps } from './to-product-tile-card-props';
import { Skeleton } from '@/components/ui/skeleton';

type ProductSearchHit = ShopperSearch.schemas['ProductSearchHit'];

// Responsive `sizes` for the tile images, derived from the grid's column counts
// (1 column below sm, 2 from sm, 3 from lg) and whether the refinement panel consumes width at lg+.
const TILE_SIZES_WITHOUT_REFINEMENTS = '(min-width: 1024px) 33vw, (min-width: 640px) 50vw, 100vw';
const TILE_SIZES_WITH_REFINEMENTS = '(min-width: 1024px) 28vw, (min-width: 640px) 50vw, 100vw';

// Number of leading tiles loaded eagerly with high fetch priority (one row at the widest, lg, layout).
// These are the LCP candidates; the rest load lazily.
const PRIORITY_TILE_COUNT = 3;

function NoProductsMessage({ criticalSize, nonCriticalSize }: { criticalSize: number; nonCriticalSize: number }) {
    const { t } = useTranslation('common');

    if (criticalSize > 0 || nonCriticalSize > 0) {
        return null;
    }
    return (
        <div className="col-span-full text-center py-12">
            <p className="text-sm text-muted-foreground">{t('noProductsFound')}</p>
        </div>
    );
}

function Tile({
    product,
    sizes,
    priority,
    handleProductClick,
}: {
    product: ProductSearchHit;
    sizes: string;
    priority: boolean;
    handleProductClick?: (product: ProductSearchHit) => void;
}) {
    return (
        <ProductTileCard
            {...toProductTileCardProps(product)}
            price={<TilePrice product={product} />}
            sizes={sizes}
            priority={priority}
            onClick={handleProductClick ? () => handleProductClick(product) : undefined}
        />
    );
}

function TileSkeleton() {
    // Matches the real tile's ratio (448x600 in the design) so the swap doesn't shift the grid.
    return <Skeleton className="aspect-[448/600] w-full" />;
}

function ProductGridSkeleton({ count = 6 }: { count?: number }) {
    return (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-10">
            {Array.from({ length: count }, (_, index) => (
                <TileSkeleton key={`grid-skeleton-${index}`} />
            ))}
        </div>
    );
}

/**
 * ProductGrid renders the Black Magic product tiles in a responsive grid (1 column below sm, 2 from sm, 3 from lg).
 *
 * The grid accepts synchronous data for both critical (above-the-fold) and non-critical
 * (below-the-fold) products. The first row of critical tiles is loaded eagerly with high fetch
 * priority (LCP candidates); every other tile loads lazily. For deferred loading of non-critical
 * products, use DeferredProductGrid.
 *
 * Tile imagery is served from Widen's CDN and sized via URL params (no DIS / `<DynamicImage>`);
 * see `@/components/product-tile-card`.
 */
export default function ProductGrid({
    critical,
    nonCritical,
    hasRefinementsPanel = true,
    handleProductClick,
    // Accepted for call-site/API compatibility; the brand tile renders neither. The full-replace
    // tile is image + title + CTA only (no category label, no pickup indicator).
    topCategoryName: _topCategoryName,
    isLoading = false,
    skeletonCount,
    // @sfdc-extension-line SFDC_EXT_BOPIS
    showPickupAvailable: _showPickupAvailable,
}: {
    critical?: ProductSearchHit[];
    nonCritical?: ProductSearchHit[];
    hasRefinementsPanel?: boolean;
    handleProductClick?: (product: ProductSearchHit) => void;
    topCategoryName?: string;
    isLoading?: boolean;
    skeletonCount?: number;
    // @sfdc-extension-line SFDC_EXT_BOPIS
    showPickupAvailable?: boolean;
}): ReactElement {
    const criticalData = critical ?? [];
    const l = criticalData.length;
    const sizes = hasRefinementsPanel ? TILE_SIZES_WITH_REFINEMENTS : TILE_SIZES_WITHOUT_REFINEMENTS;

    const loadingSkeletonCount = Math.max(criticalData.length + (nonCritical?.length ?? 0), 4);

    if (isLoading) {
        return (
            <div data-testid="product-grid-loading-state" aria-busy>
                <ProductGridSkeleton count={loadingSkeletonCount} />
            </div>
        );
    }

    return (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-10">
            {criticalData.map((product, index) => (
                <Tile
                    key={product.productId}
                    product={product}
                    sizes={sizes}
                    priority={index < PRIORITY_TILE_COUNT}
                    handleProductClick={handleProductClick}
                />
            ))}
            {nonCritical?.map((product) => (
                <Tile
                    key={product.productId}
                    product={product}
                    sizes={sizes}
                    priority={false}
                    handleProductClick={handleProductClick}
                />
            ))}
            {(skeletonCount ?? 0) > 0 &&
                Array.from({ length: skeletonCount ?? 0 }, (_, i) => <TileSkeleton key={`skeleton-${i}`} />)}
            {!skeletonCount && <NoProductsMessage criticalSize={l} nonCriticalSize={nonCritical?.length ?? 0} />}
        </div>
    );
}
